import { LeadUiModel } from '../../shared/ui-models/lead.ui-model';

/**
 * How a lead got here, condensed to one chip.
 *
 * `source` is the **mechanism** — a form, an import, an API post — and the
 * attribution fields are the **marketing story**. They answer different halves
 * of "where did this come from", and the queue needs both without becoming a
 * spreadsheet: the chip carries the short answer, the tooltip carries every
 * value behind it.
 */

/**
 * Readable names for the service's `LeadSource` enum.
 *
 * Spelled out rather than run through `titleCase`, which would render
 * `api` as "Api" and `web_form` as "Web form" — the first is wrong and the
 * second is only right by accident.
 */
export const SOURCE_LABEL: Record<string, string> = {
  web_form: 'Web form',
  admin: 'Admin',
  api: 'API',
  file_upload: 'File upload',
  messaging: 'Messaging'
};

export const sourceLabel = (source?: string): string =>
  (source && SOURCE_LABEL[source]) || (source ? source.replace(/_/g, ' ') : '—');

/** Sentence case, matching the rest of the module. */
const sentence = (value: string): string =>
  value.replace(/_/g, ' ').replace(/^./, (c: string) => c.toUpperCase());

/**
 * The chip text: the mechanism, plus the channel when we know it.
 *
 * The channel earns the space because it is usually the answer somebody
 * actually wants — "Facebook" is more use on a queue than "web form", which
 * describes almost every lead. Where there is no channel the chip is just the
 * mechanism rather than padded with "unknown".
 */
export const sourceChipText = (lead: Pick<LeadUiModel, 'source' | 'marketingChannel'>): string => {
  const mechanism: string = sourceLabel(lead.source);
  return lead.marketingChannel ? `${mechanism} · ${sentence(lead.marketingChannel)}` : mechanism;
};

/** One attribution line, omitted entirely when the value is absent. */
const line = (label: string, value?: string | Date): string | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  return `${label}: ${value instanceof Date ? value.toLocaleString() : String(value)}`;
};

/**
 * Everything behind the chip, for the tooltip.
 *
 * Grouped rather than alphabetical: the mechanism first, then the campaign
 * someone would recognise, then the plumbing (click ids, landing path) that
 * only matters when a number looks wrong. A lead with no attribution says so
 * in one line instead of rendering a wall of blanks — most leads are organic
 * and an empty tooltip reads as a bug.
 */
export const attributionTooltip = (lead: LeadUiModel): string => {
  const head: string = `Source: ${sourceLabel(lead.source)}`;

  const campaign: string[] = [
    line('Channel', lead.marketingChannel && sentence(lead.marketingChannel)),
    line('Campaign', lead.utmCampaign),
    line('Source', lead.utmSource),
    line('Medium', lead.utmMedium),
    line('Content', lead.utmContent),
    line('Term', lead.utmTerm),
    line('Ad type', lead.adType && sentence(lead.adType)),
    line('Messaging', lead.messagingChannel && sentence(lead.messagingChannel))
  ].filter((entry: string | undefined): entry is string => Boolean(entry));

  const plumbing: string[] = [
    line('Landing page', lead.landingPath),
    line('Referrer', lead.referrer),
    line('fbclid', lead.fbclid),
    line('gclid', lead.gclid),
    line('Attributed', lead.attributedAt)
  ].filter((entry: string | undefined): entry is string => Boolean(entry));

  if (!campaign.length && !plumbing.length) return `${head}\nNo campaign recorded`;

  return [head, ...campaign, ...plumbing].join('\n');
};
